import { useState, useCallback, useEffect, type FC, type FormEvent } from "react";
import ObjectiveLanguageEvaluator from "@/components/feature/ObjectiveLanguageEvaluator";
import { evaluateText, LEVEL_CONFIG } from "@/components/feature/ObjectiveLanguageEvaluator";

// ── Section definitions ──
interface PreguntaGuia {
  id: string;
  pregunta: string;
  placeholder: string;
}

interface SeccionFormulario {
  id: string;
  titulo: string;
  icono: string;
  descripcion: string;
  preguntas: PreguntaGuia[];
}

const SECCIONES: SeccionFormulario[] = [
  {
    id: "identificacion",
    titulo: "Identificación",
    icono: "ri-user-line",
    descripcion: "Datos de identificación del estudiante y del docente que informa.",
    preguntas: [
      { id: "ident_nombre", pregunta: "Nombre completo del estudiante", placeholder: "Ej: Vicente Díaz Leiva" },
      { id: "ident_curso", pregunta: "Curso", placeholder: "Ej: 3° Medio C" },
      { id: "ident_profesor", pregunta: "Profesor que informa", placeholder: "Su nombre completo" },
      { id: "ident_asignatura", pregunta: "Asignatura o jefatura", placeholder: "Ej: Profesor Jefe / Matemática" },
      { id: "ident_periodo", pregunta: "Periodo observado", placeholder: "Ej: Primer semestre 2026 (marzo - junio)" },
      { id: "ident_fecha", pregunta: "Fecha del informe", placeholder: "Ej: 10 de junio de 2026" },
      { id: "ident_solicitante", pregunta: "Solicitante", placeholder: "Ej: Valentina Soto, Orientadora" },
      { id: "ident_motivo", pregunta: "Motivo general de la solicitud", placeholder: "Describa brevemente el motivo por el cual se solicita este informe" },
    ],
  },
  {
    id: "rendimiento",
    titulo: "Rendimiento académico",
    icono: "ri-bar-chart-2-line",
    descripcion: "Desempeño académico del estudiante en el periodo informado. Use datos objetivos: notas, porcentajes de cumplimiento, evaluaciones pendientes.",
    preguntas: [
      { id: "rend_desempeno", pregunta: "¿Cómo ha sido el desempeño académico del estudiante en el periodo informado?", placeholder: "Describa usando datos objetivos: notas parciales, promedio general, comparación con periodos anteriores." },
      { id: "rend_fortalezas", pregunta: "¿En qué áreas o asignaturas presenta mayores fortalezas?", placeholder: "Mencione las asignaturas o áreas con mejor rendimiento, con datos si están disponibles." },
      { id: "rend_dificultades", pregunta: "¿En qué áreas presenta mayores dificultades?", placeholder: "Sea específico: qué asignaturas, qué tipo de dificultades, con qué evidencia." },
      { id: "rend_cumplimiento", pregunta: "¿Cumple con tareas, trabajos y evaluaciones?", placeholder: "Ej: Ha entregado 3 de 5 tareas solicitadas en mayo. No rindió la evaluación del 20/05." },
      { id: "rend_progreso", pregunta: "¿Ha mostrado progreso, estancamiento o retroceso?", placeholder: "Compare con periodo anterior si tiene datos disponibles." },
      { id: "rend_pendientes", pregunta: "¿Existen evaluaciones pendientes?", placeholder: "Detalle qué evaluaciones están pendientes y desde qué fecha." },
    ],
  },
  {
    id: "asistencia",
    titulo: "Asistencia y puntualidad",
    icono: "ri-calendar-check-line",
    descripcion: "Registro de asistencia y puntualidad basado en datos del libro de clases. Evite conclusiones no respaldadas por registros.",
    preguntas: [
      { id: "asist_regularidad", pregunta: "¿El estudiante asiste regularmente a clases?", placeholder: "Indique porcentaje de asistencia del periodo y compárelo con el mínimo exigido (85%)." },
      { id: "asist_atrasos", pregunta: "¿Registra atrasos frecuentes?", placeholder: "Cuantifique: cantidad de atrasos en el periodo. Especifique si son reiterados." },
      { id: "asist_inasistencias", pregunta: "¿Presenta inasistencias reiteradas?", placeholder: "Detalle: cantidad de inasistencias justificadas e injustificadas en el periodo." },
      { id: "asist_impacto", pregunta: "¿Las inasistencias afectan su proceso escolar?", placeholder: "Relacione inasistencias con rendimiento o pérdida de contenidos. Solo si hay evidencia." },
      { id: "asist_retiros", pregunta: "¿Existen retiros anticipados relevantes?", placeholder: "Registre cantidad y motivos si están documentados." },
    ],
  },
  {
    id: "comportamiento",
    titulo: "Comportamiento en aula",
    icono: "ri-user-settings-line",
    descripcion: "Observaciones conductuales objetivas. Describa hechos observables, no etiquete al estudiante.",
    preguntas: [
      { id: "comp_normas", pregunta: "¿Respeta las normas de convivencia dentro de la sala de clases?", placeholder: "Describa conductas observables. Evite frases como 'se porta mal' o 'es conflictivo'." },
      { id: "comp_pares", pregunta: "¿Cómo se relaciona con sus compañeros?", placeholder: "Describa interacciones observadas: trabajo en grupo, recreos, actividades colaborativas." },
      { id: "comp_adultos", pregunta: "¿Cómo se relaciona con los adultos del establecimiento?", placeholder: "Describa interacciones con docentes, asistentes y otros funcionarios." },
      { id: "comp_participacion", pregunta: "¿Participa de forma adecuada durante la clase?", placeholder: "Describa tipo de participación: levanta la mano, responde cuando se le solicita, interrumpe." },
      { id: "comp_interrupciones", pregunta: "¿Presenta interrupciones frecuentes durante la clase?", placeholder: "Cuantifique y describa el tipo de interrupción si existen registros." },
      { id: "comp_incidentes", pregunta: "¿Existen situaciones registradas en el libro de clases?", placeholder: "Mencione observaciones formales registradas, con fechas si están disponibles." },
      { id: "comp_instrucciones", pregunta: "¿Responde a las indicaciones e instrucciones del docente?", placeholder: "Describa la respuesta habitual del estudiante ante instrucciones y correcciones." },
    ],
  },
  {
    id: "participacion",
    titulo: "Participación y disposición al aprendizaje",
    icono: "ri-lightbulb-line",
    descripcion: "Actitud del estudiante hacia el aprendizaje, participación en clases y motivación observada.",
    preguntas: [
      { id: "part_clases", pregunta: "¿Participa activamente en clases?", placeholder: "Describa frecuencia y tipo de participación: responde preguntas, hace consultas, aporta ideas." },
      { id: "part_ayuda", pregunta: "¿Solicita ayuda cuando la necesita?", placeholder: "Describa si el estudiante pide apoyo o tiende a no solicitar ayuda cuando enfrenta dificultades." },
      { id: "part_disposicion", pregunta: "¿Muestra disposición al trabajo escolar?", placeholder: "Describa actitud hacia las actividades propuestas: inicia tareas, requiere motivación adicional." },
      { id: "part_actividades", pregunta: "¿Completa las actividades propuestas en clases?", placeholder: "Indique porcentaje aproximado de actividades completadas si tiene registro." },
      { id: "part_apoyo", pregunta: "¿Requiere apoyo frecuente del docente para realizar su trabajo?", placeholder: "Describa nivel de autonomía y tipo de apoyo requerido." },
      { id: "part_motivacion", pregunta: "¿Se observa motivación o desmotivación hacia el aprendizaje?", placeholder: "Describa indicadores observables: interés en temas, abandono de tareas, expresiones de frustración." },
    ],
  },
  {
    id: "apoderado",
    titulo: "Compromiso del apoderado",
    icono: "ri-parent-line",
    descripcion: "Registro de comunicación y participación del apoderado. Solo use hechos documentados, evite juicios sobre la familia.",
    preguntas: [
      { id: "apo_reuniones", pregunta: "¿El apoderado asiste a reuniones de curso?", placeholder: "Registre cantidad de asistencias y fechas si están disponibles." },
      { id: "apo_citaciones", pregunta: "¿Responde a las citaciones realizadas por el establecimiento?", placeholder: "Detalle qué citaciones se realizaron y si hubo respuesta." },
      { id: "apo_comunicacion", pregunta: "¿Mantiene comunicación con el colegio?", placeholder: "Describa canales y frecuencia de comunicación si hay registro." },
      { id: "apo_acuerdos", pregunta: "¿Cumple los acuerdos establecidos con el establecimiento?", placeholder: "Mencione acuerdos específicos y su estado de cumplimiento." },
      { id: "apo_apoyo", pregunta: "¿Apoya el proceso escolar del estudiante?", placeholder: "Describa indicadores observables: firma de comunicaciones, asistencia a actividades, seguimiento de tareas." },
      { id: "apo_contacto", pregunta: "¿Existen registros de contacto con el apoderado?", placeholder: "Detalle fechas, medio (presencial, telefónico, correo) y resultado del contacto." },
    ],
  },
  {
    id: "medidas",
    titulo: "Medidas de apoyo aplicadas",
    icono: "ri-hand-heart-line",
    descripcion: "Apoyos brindados al estudiante desde el ámbito docente y escolar.",
    preguntas: [
      { id: "med_apoyos", pregunta: "¿Qué apoyos específicos se han brindado al estudiante?", placeholder: "Detalle tipo de apoyo, frecuencia, responsable y duración." },
      { id: "med_entrevistas", pregunta: "¿Se han realizado entrevistas con el estudiante o apoderado?", placeholder: "Registre fechas de entrevistas, participantes y acuerdos alcanzados." },
      { id: "med_derivaciones", pregunta: "¿Se ha derivado al estudiante a orientación, convivencia o psicología?", placeholder: "Indique si hubo derivación, a qué área y resultado si está disponible." },
      { id: "med_adecuaciones", pregunta: "¿Se han realizado adecuaciones curriculares o apoyos pedagógicos?", placeholder: "Describa tipo de adecuación, asignatura y resultados observados." },
      { id: "med_acuerdos", pregunta: "¿Se establecieron acuerdos formales con el estudiante?", placeholder: "Detalle acuerdos, fecha y nivel de cumplimiento." },
      { id: "med_seguimiento", pregunta: "¿Se ha realizado seguimiento de las medidas aplicadas?", placeholder: "Describa método de seguimiento y resultados registrados." },
    ],
  },
  {
    id: "observaciones",
    titulo: "Observaciones relevantes",
    icono: "ri-file-list-3-line",
    descripcion: "Antecedentes adicionales que el equipo solicitante deba conocer. Mantenga un tono formal, prudente y objetivo.",
    preguntas: [
      { id: "obs_antecedentes", pregunta: "¿Existe algún antecedente relevante que deba ser informado?", placeholder: "Mencione solo información pertinente a la solicitud. Evite detalles innecesarios." },
      { id: "obs_cambios", pregunta: "¿Ha habido cambios significativos en el comportamiento o rendimiento?", placeholder: "Describa cambios observados, posible contexto y periodo en que ocurrieron." },
      { id: "obs_seguimiento", pregunta: "¿Qué aspectos requieren seguimiento por parte del equipo solicitante?", placeholder: "Señale áreas que necesitan atención continua o intervención adicional." },
      { id: "obs_informar", pregunta: "¿Qué información considera importante que conozca el equipo solicitante?", placeholder: "Resuma los puntos clave que el equipo debe considerar para la toma de decisiones." },
    ],
  },
];

// ── Simulated AI rewrite ──
function simulateAIRewrite(text: string, pregunta: string): string {
  if (!text.trim()) return text;

  const fragments = evaluateText(text);
  const hasRed = fragments.some((f) => f.level === "rojo");

  if (hasRed) {
    // Try to apply suggestions
    let rewritten = text;
    for (const f of fragments) {
      if (f.level === "rojo" && f.suggestion && !f.suggestion.includes("[")) {
        rewritten = rewritten.replace(f.text, f.suggestion);
      }
    }
    if (rewritten !== text) return rewritten;
  }

  // Add temporal context if missing
  if (!text.toLowerCase().includes("durante") && !text.toLowerCase().includes("periodo")) {
    return `Durante el periodo informado, ${text.charAt(0).toLowerCase() + text.slice(1)}`;
  }

  return text;
}

// ── AI Improvement Messages ──
function generateAISuggestion(text: string): string | null {
  if (!text.trim()) return null;
  const fragments = evaluateText(text);
  if (fragments.length === 0) return null;

  const redFrags = fragments.filter((f) => f.level === "rojo");
  const yellowFrags = fragments.filter((f) => f.level === "amarillo");

  const lines: string[] = [];

  if (redFrags.length > 0) {
    lines.push(`Se detectaron ${redFrags.length} frase(s) que contienen lenguaje subjetivo o no respaldado:`);
    redFrags.forEach((f) => {
      lines.push(`• "${f.text}" → ${f.reason}`);
    });
  }

  if (yellowFrags.length > 0) {
    if (lines.length > 0) lines.push("");
    lines.push(`Se detectaron ${yellowFrags.length} frase(s) que requieren mayor precisión:`);
    yellowFrags.forEach((f) => {
      lines.push(`• "${f.text}" → ${f.reason}`);
    });
  }

  if (text.length < 30) {
    lines.push("La respuesta es muy breve. Considere agregar más detalles y datos específicos.");
  }

  return lines.join("\n");
}

// ── Component ──
interface Props {
  className?: string;
  onBack?: () => void;
  datosIniciales?: Record<string, string>;
  onChange?: (data: Record<string, string>) => void;
}

const GuidedReportForm: FC<Props> = ({ className = "", onBack, datosIniciales = {}, onChange }) => {
  const [seccionActiva, setSeccionActiva] = useState(0);
  const [respuestas, setRespuestas] = useState<Record<string, string>>(datosIniciales);

  useEffect(() => {
    onChange?.(respuestas);
  }, [respuestas, onChange]);
  const [aiPanelAbierto, setAiPanelAbierto] = useState<Record<string, boolean>>({});
  const [aiLoadingPregunta, setAiLoadingPregunta] = useState<string | null>(null);
  const [aiSugerencias, setAiSugerencias] = useState<Record<string, string | null>>({});
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalContent, setAiModalContent] = useState("");

  const handleChange = useCallback((preguntaId: string, valor: string) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: valor }));
    // Clear AI suggestion when user edits
    setAiSugerencias((prev) => {
      const next = { ...prev };
      delete next[preguntaId];
      return next;
    });
  }, []);

  const handleAIRewrite = useCallback((preguntaId: string) => {
    setAiLoadingPregunta(preguntaId);
    // Simulate AI processing delay
    setTimeout(() => {
      const sec = SECCIONES[seccionActiva];
      const pregunta = sec.preguntas.find((p) => p.id === preguntaId);
      const currentText = respuestas[preguntaId] || "";
      const rewritten = simulateAIRewrite(currentText, pregunta?.pregunta || "");
      if (rewritten !== currentText) {
        setRespuestas((prev) => ({ ...prev, [preguntaId]: rewritten }));
      }
      const suggestion = generateAISuggestion(rewritten || currentText);
      setAiSugerencias((prev) => ({ ...prev, [preguntaId]: suggestion }));
      setAiLoadingPregunta(null);
    }, 800);
  }, [seccionActiva, respuestas]);

  const toggleAIPanel = (preguntaId: string) => {
    setAiPanelAbierto((prev) => ({ ...prev, [preguntaId]: !prev[preguntaId] }));
  };

  const handleViewFullAI = () => {
    const sec = SECCIONES[seccionActiva];
    const allRedFlags: string[] = [];
    let totalRed = 0;
    let totalYellow = 0;

    sec.preguntas.forEach((p) => {
      const text = respuestas[p.id] || "";
      if (text.trim()) {
        const fragments = evaluateText(text);
        const reds = fragments.filter((f) => f.level === "rojo");
        const yellows = fragments.filter((f) => f.level === "amarillo");
        totalRed += reds.length;
        totalYellow += yellows.length;
        reds.forEach((f) => allRedFlags.push(`• Sección "${sec.titulo}": "${f.text}" → ${f.reason}`));
      }
    });

    if (totalRed > 0 || totalYellow > 0) {
      setAiModalContent(
        `=== RESUMEN DE EVALUACIÓN DE LENGUAJE ===\n\n` +
        `Frases con problemas (rojo): ${totalRed}\nFrases con ambigüedad (amarillo): ${totalYellow}\n\n` +
        `Problemas detectados:\n${allRedFlags.join("\n")}\n\n` +
        `RECOMENDACIÓN GENERAL:\nRevise las frases marcadas y ajuste el lenguaje para que sea objetivo, específico y basado en datos observables.`
      );
    } else {
      setAiModalContent("No se detectaron problemas de lenguaje en esta sección. Todas las respuestas utilizan lenguaje objetivo y específico.");
    }
    setShowAIModal(true);
  };

  const seccion = SECCIONES[seccionActiva];
  const progressPct = Math.round(((seccionActiva + 1) / SECCIONES.length) * 100);

  const seccionCompleta = seccion.preguntas.every((p) => respuestas[p.id]?.trim());
  const currentSeccionRedCount = seccion.preguntas.reduce((count, p) => {
    const fragments = evaluateText(respuestas[p.id] || "");
    return count + fragments.filter((f) => f.level === "rojo").length;
  }, 0);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Progress bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-background-200/70 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-500 ease-out-expo"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="text-xs font-bold text-foreground-500 flex-shrink-0">
          Sección {seccionActiva + 1} de {SECCIONES.length}
        </span>
      </div>

      {/* Section tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
        {SECCIONES.map((sec, idx) => {
          const isActive = idx === seccionActiva;
          const isCompleted = sec.preguntas.every((p) => respuestas[p.id]?.trim());
          const hasRed = sec.preguntas.some((p) => {
            const fragments = evaluateText(respuestas[p.id] || "");
            return fragments.some((f) => f.level === "rojo");
          });

          return (
            <button
              key={sec.id}
              onClick={() => setSeccionActiva(idx)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                isActive
                  ? "bg-primary-500 text-white"
                  : isCompleted
                  ? hasRed
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-background-100 text-foreground-500 border border-transparent hover:bg-background-200"
              }`}
            >
              <span className={`flex items-center justify-center w-3 h-3`}>
                <i className={`${sec.icono} ${isActive ? "text-white" : ""} text-[11px]`} />
              </span>
              {idx + 1}
              {isCompleted && (
                <span className="flex items-center justify-center w-3 h-3">
                  <i className={`${hasRed ? "ri-close-line text-red-500" : "ri-check-line text-emerald-500"} text-[11px]`} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Current section */}
      <div className="card-modern p-5 md:p-6">
        {/* Section header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h3 className="text-lg font-bold text-foreground-900 flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5 text-primary-500">
                <i className={`${seccion.icono} text-lg`} />
              </span>
              {seccion.titulo}
            </h3>
            <p className="text-sm text-foreground-500 mt-1">{seccion.descripcion}</p>
            {currentSeccionRedCount > 0 && (
              <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                <span className="flex items-center justify-center w-3 h-3">
                  <i className="ri-error-warning-fill text-red-500 text-xs" />
                </span>
                {currentSeccionRedCount} frase(s) requieren ajuste de lenguaje
              </p>
            )}
          </div>
          <button
            onClick={handleViewFullAI}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-50 border border-accent-200/50 text-accent-700 text-xs font-medium hover:bg-accent-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-magic-line" />
            </span>
            Evaluar sección
          </button>
        </div>

        {/* Questions */}
        <div className="space-y-5">
          {seccion.preguntas.map((pregunta) => {
            const currentText = respuestas[pregunta.id] || "";
            const fragments = currentText.trim() ? evaluateText(currentText) : [];
            const hasRed = fragments.some((f) => f.level === "rojo");
            const hasYellow = fragments.some((f) => f.level === "amarillo");
            const isAIPanelOpen = aiPanelAbierto[pregunta.id] || false;
            const isAILoading = aiLoadingPregunta === pregunta.id;
            const aiSuggestion = aiSugerencias[pregunta.id];

            return (
              <div key={pregunta.id} className="space-y-2.5">
                <label className="block text-sm font-semibold text-foreground-800" htmlFor={pregunta.id}>
                  {pregunta.pregunta}
                </label>

                <div className="relative">
                  <textarea
                    id={pregunta.id}
                    value={currentText}
                    onChange={(e) => handleChange(pregunta.id, e.target.value)}
                    placeholder={pregunta.placeholder}
                    rows={3}
                    maxLength={500}
                    className="w-full bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 outline-none transition-all duration-200 px-4 py-3 resize-none focus:bg-white focus:border-primary-300 focus:ring-2 focus:ring-primary-200/50"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-foreground-400">{currentText.length}/500 caracteres</span>
                    <div className="flex items-center gap-2">
                      {/* Color indicator dots */}
                      {currentText.trim() && (
                        <div className="flex items-center gap-1">
                          {hasRed && <span className="w-2 h-2 rounded-full bg-red-500" title="Lenguaje subjetivo detectado" />}
                          {hasYellow && <span className="w-2 h-2 rounded-full bg-amber-500" title="Frase ambigua detectada" />}
                          {!hasRed && !hasYellow && <span className="w-2 h-2 rounded-full bg-emerald-500" title="Lenguaje objetivo" />}
                        </div>
                      )}
                      {/* AI buttons */}
                      {currentText.trim() && (
                        <>
                          <button
                            onClick={() => handleAIRewrite(pregunta.id)}
                            disabled={isAILoading}
                            className="flex items-center gap-1 text-[11px] font-medium text-accent-600 hover:text-accent-700 cursor-pointer disabled:opacity-50"
                          >
                            <span className="flex items-center justify-center w-3 h-3">
                              {isAILoading ? (
                                <span className="w-3 h-3 border-2 border-accent-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <i className="ri-magic-line" />
                              )}
                            </span>
                            {isAILoading ? "Mejorando..." : "Mejorar redacción IA"}
                          </button>
                          <button
                            onClick={() => toggleAIPanel(pregunta.id)}
                            className="flex items-center gap-1 text-[11px] font-medium text-foreground-500 hover:text-foreground-700 cursor-pointer"
                          >
                            <span className="flex items-center justify-center w-3 h-3">
                              <i className={`ri-${isAIPanelOpen ? "eye-off" : "eye"}-line`} />
                            </span>
                            Evaluar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* AI evaluation panel */}
                {isAIPanelOpen && currentText.trim() && (
                  <div className="animate-slide-down">
                    <ObjectiveLanguageEvaluator text={currentText} compact />
                    {aiSuggestion && (
                      <div className="mt-2 p-3 rounded-lg bg-accent-50/80 border border-accent-200/50">
                        <p className="text-[11px] font-bold text-accent-700 flex items-center gap-1 mb-1">
                          <span className="flex items-center justify-center w-3 h-3">
                            <i className="ri-robot-2-line text-accent-500" />
                          </span>
                          Asistente IA
                        </p>
                        <p className="text-xs text-foreground-600 whitespace-pre-line">{aiSuggestion}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-background-200/60">
          {onBack ? (
            <button
              onClick={onBack}
              className="btn-modern bg-background-100 text-foreground-600 hover:bg-background-200 px-4 py-2 text-sm cursor-pointer"
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-arrow-left-line" />
              </span>
              Volver
            </button>
          ) : (
            <button
              onClick={() => setSeccionActiva(Math.max(0, seccionActiva - 1))}
              disabled={seccionActiva === 0}
              className="btn-modern bg-background-100 text-foreground-600 hover:bg-background-200 px-4 py-2 text-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-arrow-left-line" />
              </span>
              Anterior
            </button>
          )}

          {seccionActiva < SECCIONES.length - 1 ? (
            <button
              onClick={() => setSeccionActiva(seccionActiva + 1)}
              className="btn-modern-primary px-5 py-2 text-sm cursor-pointer"
            >
              Siguiente sección
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-arrow-right-line" />
              </span>
            </button>
          ) : (
            <span className="badge-modern bg-emerald-50 text-emerald-700 border border-emerald-200/50 text-xs">
              Última sección
            </span>
          )}
        </div>
      </div>

      {/* AI Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowAIModal(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl shadow-soft-lg w-full max-w-lg max-h-[80vh] flex flex-col animate-scale-in border border-background-200/60 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent-100">
                  <i className="ri-magic-line text-accent-600" />
                </span>
                <h3 className="text-sm font-bold text-foreground-900">Evaluación de lenguaje objetivo</h3>
              </div>
              <button
                onClick={() => setShowAIModal(false)}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-400 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              <pre className="text-xs text-foreground-700 whitespace-pre-wrap font-sans leading-relaxed">{aiModalContent}</pre>
            </div>
            <div className="px-5 py-3 border-t border-background-200/60 flex justify-end">
              <button
                onClick={() => setShowAIModal(false)}
                className="btn-modern-primary px-4 py-2 text-sm cursor-pointer"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedReportForm;
export { SECCIONES, simulateAIRewrite, generateAISuggestion };